/**
 * The work a developer sent to their own agent, and the three ways to send more: once, until it
 * is done, or on a schedule. The same screen in the browser and in the desktop app — the work goes
 * out through remote control either way, to whichever of the person's agents is listening.
 */

import { useState, type FormEvent } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FileText, FolderGit2, Pause, Pencil, Play, Square, Zap } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { Badge, EmptyState, ErrorBanner, Skeleton } from '../components/primitives'
import { Modal } from '../components/Modal'
import { SendWorkDialog } from '../components/SendWorkDialog'
import SchedulePicker from '../components/SchedulePicker'
import { describeSchedule, draftOf, scheduleRequest, type ScheduleDraft } from '../lib/schedule'
import type { WorkOrder, WorkOrderKind, WorkOrderStatus } from '../lib/types'

/** The refusals a loop's buttons can meet, said in words (docs/refactor/66). */
const LOOP_ERRORS = ['already_running', 'finished', 'not_a_loop', 'plan_limit', 'remote_off', 'invalid_cron', 'invalid_timezone', 'too_often', 'invalid_interval', 'required', 'too_long']

const TONE: Record<WorkOrderStatus, 'accent' | 'ok' | 'neutral' | 'danger' | 'info'> = {
  Running: 'accent',
  Done: 'ok',
  Stopped: 'neutral',
  Failed: 'danger',
  Waiting: 'info',
}

export default function WorkOrders() {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  // Ctrl+K "Agendar uma tarefa" lands here with ?schedule=1: the dialog opens already on a loop.
  const [params, setParams] = useSearchParams()
  const [sending, setSending] = useState<WorkOrderKind | null>(params.get('schedule') ? 'Scheduled' : null)
  const [editing, setEditing] = useState<WorkOrder | null>(null)
  const [loopProblem, setLoopProblem] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['work-orders'],
    queryFn: api.workOrders.list,
    // A step ends when the agent answers; the screen follows along.
    refetchInterval: 5_000,
  })

  const stop = useMutation({
    mutationFn: (order: WorkOrder) => api.workOrders.stop(order.id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-orders'] }),
  })

  // Managing a loop: pause, resume, run once now.
  const manage = useMutation({
    mutationFn: ({ order, action }: { order: WorkOrder; action: 'pause' | 'resume' | 'runNow' }) => api.workOrders[action](order.id),
    onMutate: () => setLoopProblem(null),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['work-orders'] }),
    onError: (e) => setLoopProblem(e instanceof ApiError && e.code && LOOP_ERRORS.includes(e.code) ? t(`work.error.${e.code}`) : t('error.generic')),
  })

  const notDeveloper = error instanceof ApiError && error.status === 403

  return (
    <div className="stack">
      <div className="row row--between row--wrap">
        <p className="muted small" style={{ maxWidth: 720, margin: 0 }}>{t('work.intro')}</p>
        {/* One door (41, A8): the dialog asks how — once, until done, or on a schedule. */}
        <button type="button" className="btn btn--primary" onClick={() => setSending('Once')} disabled={notDeveloper}>
          <Play size={14} /> {t('work.sendWork')}
        </button>
      </div>

      {notDeveloper && <ErrorBanner message={t('work.notDeveloper')} />}
      {error && !notDeveloper && <ErrorBanner message={t('error.generic')} onRetry={() => void refetch()} retryLabel={t('common.retry')} />}
      {isLoading && <Skeleton height={120} />}

      {data && data.length === 0 && <EmptyState title={t('work.emptyTitle')} body={t('work.emptyBody')} />}

      {loopProblem && <ErrorBanner message={loopProblem} />}

      {data && data.length > 0 && (
        <div className="repo-list">
          {data.map((order) => (
            <OrderRow
              key={order.id}
              order={order}
              onStop={() => stop.mutate(order)}
              onLoop={(action) => manage.mutate({ order, action })}
              onEdit={() => setEditing(order)}
              busy={manage.isPending}
            />
          ))}
        </div>
      )}

      {sending && (
        <SendWorkDialog
          kind={sending}
          onClose={() => {
            setSending(null)
            if (params.get('schedule')) setParams({}, { replace: true })
          }}
        />
      )}
      {editing && <EditLoopDialog order={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}

function OrderRow({ order, onStop, onLoop, onEdit, busy }: {
  order: WorkOrder
  onStop: () => void
  onLoop: (action: 'pause' | 'resume' | 'runNow') => void
  onEdit: () => void
  busy: boolean
}) {
  const { t, locale } = useI18n()
  const active = order.status === 'Running' || order.status === 'Waiting'
  const loop = order.kind === 'Scheduled' && active
  const paused = loop && !!order.pausedAt
  const time = (iso: string) => new Date(iso).toLocaleString(locale, { dateStyle: 'short', timeStyle: 'short' })

  return (
    <div className="repo-row panel work-row">
      <div className="stack" style={{ gap: 4, minWidth: 0, flex: 1 }}>
        <div className="row row--tight row--wrap">
          <strong className="truncate" title={order.instructions}>{order.title}</strong>
          <Badge tone="neutral">{t(`work.kind.${order.kind}`)}</Badge>
          {paused ? <Badge tone="neutral">{t('work.status.Paused')}</Badge> : <Badge tone={TONE[order.status]}>{t(`work.status.${order.status}`)}</Badge>}
        </div>

        <div className="row row--tight row--wrap tiny muted">
          {order.kind === 'UntilDone' && (
            <span>
              {order.phase === 'Check' ? t('work.checkerRound', { round: order.iteration, max: order.maxIterations }) : t('work.working')}
            </span>
          )}
          {order.kind === 'Scheduled' && (order.intervalMinutes || order.cron) && (
            <span title={order.cron ? `${order.cron} (${order.timeZone ?? 'UTC'})` : undefined}>
              {describeSchedule(t, locale, order)}
              {order.nextRunAt && active && !paused ? ` · ${t('work.nextRun', { at: time(order.nextRunAt) })}` : ''}
            </span>
          )}
          {order.repositoryName && (
            <span className="row row--tight">
              <FolderGit2 size={11} /> {order.repositoryName}
            </span>
          )}
          {order.docPath && (
            <span className="row row--tight mono">
              <FileText size={11} /> {order.docPath}
            </span>
          )}
          <span>{time(order.createdAt)}</span>
        </div>

        {order.lastReport && <span className="small">{t('work.lastReport', { report: order.lastReport })}</span>}
        {order.endReason && <span className="tiny muted">{t(`work.reason.${order.endReason}`)}</span>}
      </div>

      {active && (
        <div className="row row--tight row--wrap" style={{ justifyContent: 'flex-end' }}>
          {loop && (
            <>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => onLoop('runNow')} disabled={busy} title={t('work.runNowHint')}>
                <Zap size={12} /> {t('work.runNow')}
              </button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => onLoop(paused ? 'resume' : 'pause')} disabled={busy}>
                {paused ? <Play size={12} /> : <Pause size={12} />} {paused ? t('work.resume') : t('work.pause')}
              </button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={onEdit}>
                <Pencil size={12} /> {t('common.edit')}
              </button>
            </>
          )}
          <button type="button" className="btn btn--sm btn--ghost" onClick={onStop}>
            <Square size={12} /> {t('work.stop')}
          </button>
        </div>
      )}
    </div>
  )
}

/** "Editar" on a loop: what it says and when it runs, through the same picker as creating it. */
function EditLoopDialog({ order, onClose }: { order: WorkOrder; onClose: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [instructions, setInstructions] = useState(order.instructions)
  const [schedule, setSchedule] = useState<ScheduleDraft>(() => draftOf(order))
  const [problem, setProblem] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: () => api.workOrders.update(order.id, { instructions: instructions.trim(), ...scheduleRequest(schedule) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['work-orders'] })
      onClose()
    },
    onError: (e) => setProblem(e instanceof ApiError && e.code && LOOP_ERRORS.includes(e.code) ? t(`work.error.${e.code}`) : t('error.generic')),
  })

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    if (!instructions.trim()) return setProblem(t('work.error.required'))
    save.mutate()
  }

  return (
    <Modal title={t('work.editLoop')} onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <label className="field">
          <span className="field__label">{t('work.instructions')}</span>
          <textarea className="input" rows={5} value={instructions} onChange={(e) => setInstructions(e.target.value)} />
        </label>
        <SchedulePicker value={schedule} onChange={setSchedule} />
        {problem && <ErrorBanner message={problem} />}
        <div className="row row--end">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>{t('common.save')}</button>
        </div>
      </form>
    </Modal>
  )
}
